import React from 'react';
import { useForm } from 'react-hook-form';

// Helper to format date for datetime-local input (in local timezone)
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  // Format as YYYY-MM-DDTHH:MM in local timezone
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const TournamentForm = ({ tournament, onSubmit, onCancel, isLoading }) => {
  const { register, handleSubmit, formState: { errors }, watch } = useForm({
    defaultValues: tournament ? {
      ...tournament,
      start_date: formatDateForInput(tournament.start_date),
      end_date: formatDateForInput(tournament.end_date),
    } : {}
  });

  const selectedFormat = watch('format');

  // Transform form data before submit - convert local time to UTC
  const handleFormSubmit = (data) => {
    const start_date_utc = data.start_date
      ? new Date(data.start_date).toISOString()
      : null;
    const end_date_utc = data.end_date
      ? new Date(data.end_date).toISOString()
      : null;

    onSubmit({
      ...data,
      start_date: start_date_utc,
      end_date: end_date_utc,
    });
  };

  // Default overs by format for display hint
  const formatDefaults = {
    T10: { overs: 10, maxBowlerOvers: 2 },
    T20: { overs: 20, maxBowlerOvers: 4 },
    ODI: { overs: 50, maxBowlerOvers: 10 },
    TEST: { overs: null, maxBowlerOvers: null },
    CUSTOM: { overs: null, maxBowlerOvers: null },
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 border border-gray-200">
      <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">
        {tournament ? 'Edit Tournament' : 'Create New Tournament'}
      </h2>
      
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tournament Name *
          </label>
          <input
            {...register('name', { required: 'Tournament name is required' })}
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            placeholder="Enter tournament name"
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            {...register('description')}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            placeholder="Enter tournament description"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Format *
            </label>
            <select
              {...register('format', { required: 'Format is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="">Select format</option>
              <option value="T20">T20</option>
              <option value="ODI">ODI</option>
              <option value="TEST">TEST</option>
              <option value="T10">T10</option>
              <option value="CUSTOM">Custom</option>
            </select>
            {errors.format && (
              <p className="text-red-500 text-sm mt-1">{errors.format.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Venue *
            </label>
            <input
              {...register('venue', { required: 'Venue is required' })}
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
              placeholder="Enter venue name"
            />
            {errors.venue && (
              <p className="text-red-500 text-sm mt-1">{errors.venue.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date *
            </label>
            <input
              {...register('start_date', { required: 'Start date is required' })}
              type="datetime-local"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            />
            {errors.start_date && (
              <p className="text-red-500 text-sm mt-1">{errors.start_date.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date *
            </label>
            <input
              {...register('end_date', { required: 'End date is required' })}
              type="datetime-local"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            />
            {errors.end_date && (
              <p className="text-red-500 text-sm mt-1">{errors.end_date.message}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Prize Money (₹)
          </label>
          <input
            {...register('prize_money', { min: 0 })}
            type="number"
            step="0.01"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            placeholder="Enter prize money amount in Rs"
          />
          {errors.prize_money && (
            <p className="text-red-500 text-sm mt-1">{errors.prize_money.message}</p>
          )}
        </div>

        {/* Custom Overs Configuration */}
        <div className="border-t border-gray-200 pt-4 mt-4">
          <h3 className="text-md font-semibold text-gray-800 mb-3">
            Custom Overs Configuration
            <span className="text-sm font-normal text-gray-500 ml-2">(Optional)</span>
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Leave empty to use format defaults
            {selectedFormat && formatDefaults[selectedFormat] && (
              <span className="ml-1">
                ({selectedFormat}: {formatDefaults[selectedFormat].overs || 'Unlimited'} overs,
                {formatDefaults[selectedFormat].maxBowlerOvers || '∞'} overs/bowler)
              </span>
            )}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Overs Per Innings
              </label>
              <input
                {...register('overs_per_innings', {
                  min: { value: 1, message: 'Minimum 1 over' },
                  max: { value: 90, message: 'Maximum 90 overs' }
                })}
                type="number"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
                placeholder={selectedFormat && formatDefaults[selectedFormat] ?
                  `Default: ${formatDefaults[selectedFormat].overs || 'Unlimited'}` :
                  'e.g., 15, 25, 30'}
              />
              {errors.overs_per_innings && (
                <p className="text-red-500 text-sm mt-1">{errors.overs_per_innings.message}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Custom number of overs per innings (1-90)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Overs Per Bowler
              </label>
              <input
                {...register('max_overs_per_bowler', {
                  min: { value: 1, message: 'Minimum 1 over' },
                  max: { value: 20, message: 'Maximum 20 overs' }
                })}
                type="number"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
                placeholder={selectedFormat && formatDefaults[selectedFormat] ?
                  `Default: ${formatDefaults[selectedFormat].maxBowlerOvers || 'Unlimited'}` :
                  'e.g., 3, 5, 6'}
              />
              {errors.max_overs_per_bowler && (
                <p className="text-red-500 text-sm mt-1">{errors.max_overs_per_bowler.message}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Maximum overs a single bowler can bowl (1-20)
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 sm:space-x-4 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="order-2 sm:order-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="order-1 sm:order-2 px-4 py-2 bg-cricket-green text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Saving...' : (tournament ? 'Update Tournament' : 'Create Tournament')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TournamentForm;
